import { COMPLETED_SHORTCODE, EMOJI_QUERY, emojiForShortcode, type EmojiIndex } from "@/lib/emoji-index";
import { SYMBOL_SHORTCUTS, endsInUrl, hasOpenInlineCode } from "@/lib/symbol-shortcut-rules";

/**
 * The rich editors' symbol shortcuts and `:` emoji rules as plain string
 * logic, for `<input>` and `<textarea>` fields (BRE-8381). Every function takes
 * the field's text up to a collapsed caret.
 */

export interface TextReplacement {
  from: number;
  to: number;
  replacement: string;
  /** What Backspace right after the replacement puts back. */
  typed: string;
}

/** The symbol shortcut the last typed character completes, if any. */
export function symbolShortcutAt(textBeforeCaret: string): TextReplacement | null {
  for (const { find, replacement } of SYMBOL_SHORTCUTS) {
    const match = find.exec(textBeforeCaret);
    if (!match) continue;
    const from = textBeforeCaret.length - match[0].length;
    const before = textBeforeCaret.slice(0, from);
    if (hasOpenInlineCode(before) || endsInUrl(before)) return null;
    // `↔` is two conversions deep; undo it to what was typed, as `restoreDoubleArrow` does.
    const typed = replacement === "↔" ? "<->" : match[0];
    return { from, to: textBeforeCaret.length, replacement, typed };
  }
  return null;
}

/** A `:shortcode:` just closed by its second `:`, converted once the emoji data has loaded. */
export function completedShortcodeAt(textBeforeCaret: string, index: EmojiIndex): TextReplacement | null {
  const match = COMPLETED_SHORTCODE.exec(textBeforeCaret);
  if (!match) return null;
  const emoji = emojiForShortcode(index, match[2]);
  if (!emoji) return null;
  const from = textBeforeCaret.length - match[1].length;
  if (hasOpenInlineCode(textBeforeCaret.slice(0, from))) return null;
  return { from, to: textBeforeCaret.length, replacement: emoji, typed: match[1] };
}

/**
 * The `:` token being typed, at the start of the text or after whitespace (the
 * Suggestion plugin's `allowedPrefixes: [" "]`), so `Note: x`, `10:30` and
 * `https://` never count. `query` may still be too short to search.
 */
export function shortcodeTokenAt(textBeforeCaret: string): { from: number; query: string } | null {
  const match = /(?:^|\s):([^\s:]*)$/.exec(textBeforeCaret);
  if (!match) return null;
  const from = textBeforeCaret.length - match[1].length - 1;
  if (hasOpenInlineCode(textBeforeCaret.slice(0, from))) return null;
  return { from, query: match[1] };
}

/** The open `:query` the emoji menu searches for, or null. */
export function emojiQueryAt(textBeforeCaret: string): { from: number; query: string } | null {
  const token = shortcodeTokenAt(textBeforeCaret);
  return token && EMOJI_QUERY.test(token.query) ? token : null;
}
